import React from "react";
import { NavLink } from "react-router-dom";
import { useGetUsersQuery } from "../usersApiSlice";
import { UserObject } from "../../../shared/types";
import GuideCard, { GuidePersona } from "./GuideCard";
import LoadingPage from "../../../shared/LoadingPage";
import Eyebrow from "../../../shared/design/Eyebrow";
import Reveal from "../../../shared/design/Reveal";
import TeamHero from "../../../assets/bg-rockys.png";
import JoinImg from "../../../assets/home-cta-1.jpg";

// region/specialty/bio aren't in the user model — presentational personas mapped onto
// real lead-guide records by index (todo #11 decision (b)). Copy adapted from the mockup.
const PERSONAS: GuidePersona[] = [
  {
    region: "Canadian Rockies",
    spec: "Alpine traverses & glacier travel",
    bio: "Eight seasons on the Wapta Icefield — and can name every peak on the skyline before breakfast.",
  },
  {
    region: "Desert Southwest",
    spec: "Canyon country & red rock",
    bio: "A former park ranger at Arches who reads slot canyons like a map and knows exactly when the light hits.",
  },
  {
    region: "Pacific Coast",
    spec: "Coastal trails & sea kayaking",
    bio: "A Big Sur local and certified sea-kayak instructor with more sunrise paddles logged than anyone can count.",
  },
  {
    region: "Yellowstone",
    spec: "Wildlife & backcountry ecology",
    bio: "A wildlife biologist by training who can track a wolf pack and tell the story of every thermal basin.",
  },
  {
    region: "Hawaiian Islands",
    spec: "Volcanic terrain & waterfalls",
    bio: "Maui-raised and endlessly stoked — leads the Hana waterfall routes and free-dives on days off.",
  },
  {
    region: "Alaska / Denali",
    spec: "Expedition mountaineering",
    bio: "Four Denali summits. If the weather turns, exactly who you want making the call.",
  },
  {
    region: "Olympic Peninsula",
    spec: "Rainforest & alpine crossover",
    bio: "Splits life between mossy old-growth and high ridgelines, and packs the best trail coffee in the company.",
  },
  {
    region: "Mojave & Great Basin",
    spec: "Dune systems & night skies",
    bio: "A certified Dark-Sky guide who turns a desert night into an astronomy lesson you'll never forget.",
  },
];

const VALUES = [
  {
    icon: "🧭",
    title: "Local by birth",
    body: "Every guide leads in the country they grew up in. They know which trail goes quiet when the crowds arrive.",
  },
  {
    icon: "⛑️",
    title: "Trained for anything",
    body: "Wilderness first-responder certified, avalanche-aware, and re-tested every season. Your safety is never improvised.",
  },
  {
    icon: "📖",
    title: "Storytellers first",
    body: "The geology, the wildlife, the history under your boots — a Wander guide turns a hike into a narrative.",
  },
];

const Stat = ({
  figure,
  label,
}: {
  figure: React.ReactNode;
  label: string;
}) => (
  <div>
    <div className="font-serif text-[34px] leading-none text-secondary-400 md:text-[38px]">
      {figure}
    </div>
    <div className="mt-1 text-[13px] text-muted">{label}</div>
  </div>
);

const OurTeam = () => {
  const { data, isLoading, isError } = useGetUsersQuery("role=lead-guide");
  const guides: UserObject[] = data?.data?.data ?? [];

  if (isLoading) return <LoadingPage />;

  return (
    <div className="mx-auto max-w-[1320px]">
      {/* INTRO */}
      <section className="px-6 pb-8 pt-24 md:px-12 md:pt-28">
        <div className="grid items-center gap-10 md:grid-cols-[1.1fr_1fr] md:gap-14">
          <div>
            <span className="mb-6 inline-flex items-center rounded-full bg-secondary-400/10 px-3.5 py-[7px] text-[12px] font-bold uppercase tracking-[0.06em] text-secondary-400">
              The Dream Team
            </span>
            <h1 className="mb-5 font-serif text-[42px] font-normal leading-[1.02] tracking-[-0.02em] text-ink md:text-[58px]">
              Meet the people
              <br />
              who know the way.
            </h1>
            <p className="mb-7 max-w-[480px] text-[17px] leading-relaxed text-muted-200 md:text-[18px]">
              Our lead guides are the passionate navigators behind every Wander
              expedition — born locals, certified mountaineers, and storytellers
              who've spent their lives reading the land you're about to fall in
              love with.
            </p>
            <div className="flex gap-10">
              <Stat figure={guides.length || 8} label="Lead guides" />
              <Stat figure="14" label="Avg. seasons each" />
              <Stat figure="100%" label="Wilderness-certified" />
            </div>
          </div>
          <div className="relative h-[340px] overflow-hidden rounded-[24px] shadow-[0_40px_80px_-30px_rgba(19,58,69,0.5)] md:h-[420px]">
            <img
              src={TeamHero}
              alt="A Wander NA guide on a mountain ridge"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(19,58,69,0.55),transparent_55%)]" />
            <div className="absolute bottom-5 left-6 text-white">
              <div className="font-mono text-[12px] tracking-[0.08em] text-primary-500">
                SINCE 2016
              </div>
              <div className="mt-1 font-serif text-[22px]">
                Guiding North America's wild
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GUIDE GRID */}
      <section className="px-6 pb-5 pt-12 md:px-12">
        <div className="mb-8 flex items-end justify-between">
          <h2 className="font-serif text-[28px] font-normal tracking-[-0.02em] text-ink md:text-[34px]">
            Your lead guides
          </h2>
          <div className="hidden font-mono text-[12.5px] tracking-[0.04em] text-muted-300 sm:block">
            HOVER A CARD FOR THEIR STORY
          </div>
        </div>

        {isError ? (
          <p className="text-hard">Oops! Failed to load guides.</p>
        ) : guides.length ? (
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {guides.map((user, i) => (
              <Reveal key={user._id} delay={(i % 4) * 0.05}>
                <GuideCard
                  userObj={user}
                  persona={PERSONAS[i % PERSONAS.length]}
                />
              </Reveal>
            ))}
          </div>
        ) : (
          <p className="text-muted">
            No guides to show right now. Please refresh.
          </p>
        )}
      </section>

      {/* VALUES BAND */}
      <section className="px-6 pt-14 md:px-12">
        <Reveal>
          <div className="rounded-[26px] bg-secondary-400 p-9 text-white md:p-12">
            <div className="grid gap-9 md:grid-cols-3">
              {VALUES.map((v) => (
                <div key={v.title}>
                  <div className="mb-3 text-[26px]">{v.icon}</div>
                  <h3 className="mb-2 font-serif text-[23px] font-normal">
                    {v.title}
                  </h3>
                  <p className="text-[14.5px] leading-relaxed text-[#cfe0dc]">
                    {v.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* JOIN CTA */}
      <section className="px-6 pb-6 pt-12 md:px-12">
        <Reveal>
          <div className="relative overflow-hidden rounded-[26px] px-8 py-14 text-white md:px-12 md:py-16">
            <img
              src={JoinImg}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(19,58,69,0.9),rgba(19,58,69,0.3))]" />
            <div className="relative max-w-[540px]">
              <Eyebrow tone="lime" className="mb-3.5">
                WE'RE HIRING
              </Eyebrow>
              <h2 className="mb-4 font-serif text-[32px] font-normal leading-[1.06] md:text-[40px]">
                Think you belong
                <br />
                on this team?
              </h2>
              <p className="mb-7 text-[16.5px] leading-relaxed text-[#dde7e3]">
                We're always looking for certified guides who love their corner
                of the continent as much as we do. Tell us where you call wild.
              </p>
              <NavLink
                to="/careers"
                className="inline-block rounded-full bg-primary-500 px-7 py-3.5 text-[15.5px] font-bold text-ink no-underline transition-colors hover:bg-primary-300"
              >
                View open roles →
              </NavLink>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default OurTeam;
